import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

// A meta-sorban (TÉMA · NÉZŐPONT · CTA) a CTA címkét rövidítjük, hogy
// desktopon mindig egy sorba férjen ki. Egyezik a Poszt Varázsló
// META_CTA_SHORT mappingjével.
const META_CTA_SHORT = {
  "Beszélgetésindító": "BESZÉLGETÉSINDÍTÁS",
  "Ingyenes tartalom": "INGYENES",
  "Jelentkezés ajánlatra": "AJÁNLAT",
};
function shortCta(label) {
  if (!label) return "";
  return META_CTA_SHORT[label] || label;
}

/**
 * Cream-paper preview modal for saved Magnet Posts (post-facebook / post-linkedin).
 *
 * Renders the saved post text with the unified meta-row above it:
 *   TÉMA: ...  ·  NÉZŐPONT: ...  ·  CTA: ...
 *
 * The full 23+ metadata snapshot is in blueprint.topic_object and
 * blueprint.chosen_internal — these are NOT shown in the preview to keep
 * the UI calm; they're available for export and search later.
 */
export default function MagnetPostPreviewModal({ open, item, onClose }) {
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !item?.id) return;
    setLoading(true);
    setBlueprint(null);
    (async () => {
      try {
        const res = await api.get(`/asset-vault/${item.id}`);
        setBlueprint(res.data?.blueprint || null);
      } catch {
        setBlueprint(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, item?.id]);

  const platformLabel = item?.agent_type === "post-linkedin"
    ? "LinkedIn poszt"
    : item?.agent_type === "post-reels"
      ? "Instagram Reels"
      : "Facebook poszt";
  const isReels = item?.agent_type === "post-reels";
  const reelsContent = isReels && blueprint?.reels_content && typeof blueprint.reels_content === "object" ? blueprint.reels_content : null;
  const postTitle = (blueprint?.post_title || "").toString().trim();
  const tema = (blueprint?.topic_label || blueprint?.tema || "").toString().trim();
  const nezopont = (blueprint?.angle_label || blueprint?.chosen_internal?.angle_type || "").toString().trim();
  const ctaLabel = (blueprint?.cta_label || "").toString().trim();
  const teljes = (blueprint?.teljes_poszt_szoveg || "").toString().trim();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
          style={{ background: "rgba(5, 5, 10, 0.72)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
          data-testid="magnet-post-preview-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col"
            style={{
              background: "#f3eee4",
              boxShadow: "0 40px 120px rgba(0,0,0,0.5), inset 1px 0 0 rgba(255,255,255,0.5)",
            }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute"
              style={{
                top: -40, right: -40, width: 220, height: 220, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(124,108,242,0.10), transparent 70%)",
              }}
            />

            {/* Header */}
            <div className="relative flex items-start justify-between px-8 pt-7 pb-5 border-b border-[rgba(10,10,18,0.06)]">
              <div>
                <div className="mex-eyebrow mb-1">Tudástár · Mágnesposzt</div>
                <h2 className="font-heading text-2xl md:text-3xl tracking-tight" style={{ color: "#0a0a12" }}>
                  {platformLabel}
                </h2>
                {item?.saved_at && (
                  <div className="text-[11px] text-[#6a6a78] mt-1.5">
                    Mentve: {new Date(item.saved_at).toLocaleString("hu-HU")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { onClose(); navigate(`/agents/${item?.agent_type}`); }}
                  data-testid="magnet-post-preview-open-btn"
                  className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg font-medium transition"
                  style={{
                    background: "rgba(124,108,242,0.12)",
                    border: "1px solid rgba(124,108,242,0.24)",
                    color: "#3a2a4a",
                  }}
                >
                  <ArrowSquareOut size={13} weight="bold" /> Posztíróba
                </button>
                <button
                  onClick={onClose}
                  data-testid="magnet-post-preview-close-btn"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition"
                  style={{ color: "#3a2a4a", background: "rgba(10,10,18,0.04)" }}
                  aria-label="Bezárás"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="relative overflow-y-auto px-8 py-6 flex-1">
              {loading ? (
                <div className="text-[13px] text-[#6a6a78] py-10 text-center">Betöltés...</div>
              ) : !blueprint ? (
                <div className="text-[13px] text-[#6a6a78] py-10 text-center">
                  Nincs elérhető poszt.
                </div>
              ) : (
                <>
                  {/* Meta-row (unified: TÉMA · NÉZŐPONT · CTA) */}
                  {(postTitle || tema || nezopont || ctaLabel) && (
                    <div
                      style={{
                        marginTop: 48,
                        marginBottom: 36,
                        paddingBottom: 2,
                        borderBottom: "0.5px solid #26A9C9",
                      }}
                      data-testid="magnet-post-preview-meta"
                    >
                      <div style={{
                        fontSize: 26, fontWeight: 700, color: "#0a0a12",
                        letterSpacing: "-0.014em", lineHeight: 1.2,
                        marginBottom: 26,
                      }}>
                        {postTitle || tema || platformLabel}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: "#26A9C9",
                        lineHeight: 1.6,
                        display: "flex", flexWrap: "nowrap", gap: "2px 4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        letterSpacing: "0.14em",
                        fontWeight: 600,
                        fontStyle: "normal",
                        textTransform: "uppercase",
                      }}>
                        {tema && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            TÉMA: <span style={{ fontWeight: 600 }}>{tema}</span>
                          </span>
                        )}
                        {tema && nezopont && (
                          <span style={{
                            color: "#26A9C9",
                            padding: "0 5px", display: "inline-block",
                            transform: "translateY(-1px)", fontWeight: 700,
                          }}>·</span>
                        )}
                        {nezopont && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            NÉZŐPONT: <span style={{ fontWeight: 600 }}>{nezopont}</span>
                          </span>
                        )}
                        {nezopont && ctaLabel && (
                          <span style={{
                            color: "#26A9C9",
                            padding: "0 5px", display: "inline-block",
                            transform: "translateY(-1px)", fontWeight: 700,
                          }}>·</span>
                        )}
                        {ctaLabel && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            CTA: <span style={{ fontWeight: 600 }}>{shortCta(ctaLabel)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Post body */}
                  {teljes ? (
                    <pre
                      style={{
                        fontFamily: "inherit",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: 15,
                        lineHeight: 1.7,
                        color: "#1a1a28",
                        margin: 0,
                      }}
                      data-testid="magnet-post-preview-body"
                    >
                      {teljes}
                    </pre>
                  ) : (
                    <div className="text-[13px] text-[#6a6a78] py-10 text-center">
                      Üres poszt-szöveg.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="relative px-8 py-4 border-t flex items-center gap-2"
                 style={{ borderColor: "rgba(10,10,18,0.06)" }}>
              <CheckCircle size={14} color="#1d9e75" weight="fill" />
              <span className="text-[11px]" style={{ color: "#0f6e56", fontWeight: 600, letterSpacing: "0.06em" }}>
                TUDÁSTÁR
              </span>
              <span className="text-[11px] text-[#6a6a78]">- a mentett poszt szövege.</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
